/**
 * Per-project notification mute preferences (spec §12.7). Rendered as a tab
 * inside the project settings page. Fully catalog-driven: the toggles are
 * whatever `notification_event_types` currently contains, so a new server
 * event type shows up here with zero client changes.
 */
import { useMemo } from 'react'
import { BellOff } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { DynamicIcon } from '@/lib/utils/lucide-icon'
import { configFor } from '../lib/notification-catalog'
import {
  useEventCatalogQuery,
  useMutedEventTypesQuery,
  useMuteToggleMutation,
} from '../hooks/use-notifications'

const CATEGORY_LABELS_FA: Record<string, string> = {
  record: 'پرونده‌ها و بررسی',
  comment: 'نظرات',
  membership: 'عضویت',
  form: 'فرم‌ها',
}

const CATEGORY_ORDER = ['record', 'comment', 'membership', 'form']

export function NotificationPrefsContent({ projectId }: { projectId: string }) {
  const { data: catalog = [], isLoading: catalogLoading } = useEventCatalogQuery()
  const { data: muted = [], isLoading: mutedLoading } = useMutedEventTypesQuery(projectId)
  const toggle = useMuteToggleMutation(projectId)

  const mutedSet = useMemo(() => new Set(muted), [muted])
  const byCategory = useMemo(() => {
    const m = new Map<string, typeof catalog>()
    for (const t of catalog) {
      const list = m.get(t.category) ?? []
      list.push(t)
      m.set(t.category, list)
    }
    return m
  }, [catalog])

  if (catalogLoading || mutedLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-10 animate-pulse rounded bg-muted" />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
        <BellOff className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          اعلان‌های این پروژه را برای خودتان تنظیم کنید. غیرفعال‌کردن یک رویداد فقط
          اعلان‌های شما را قطع می‌کند و روی سایر اعضا اثری ندارد.
        </p>
      </div>

      {CATEGORY_ORDER.filter((c) => byCategory.has(c)).map((category) => (
        <section key={category} className="space-y-1">
          <h3 className="mb-2 text-sm font-semibold text-foreground">
            {CATEGORY_LABELS_FA[category] ?? category}
          </h3>
          <div className="divide-y divide-border rounded-lg border border-border">
            {(byCategory.get(category) ?? []).map((t) => {
              const enabled = !mutedSet.has(t.event_type)
              const id = `notif-pref-${t.event_type}`
              return (
                <div key={t.event_type} className="flex items-center gap-3 px-4 py-3">
                  <DynamicIcon
                    name={configFor(t.event_type).icon}
                    className="h-4 w-4 shrink-0 text-muted-foreground"
                  />
                  <Label htmlFor={id} className="flex-1 cursor-pointer text-sm font-normal">
                    {t.label_fa}
                    {t.default_priority === 'high' && (
                      <span className="ms-2 rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] text-destructive">
                        مهم
                      </span>
                    )}
                  </Label>
                  <Checkbox
                    id={id}
                    checked={enabled}
                    disabled={toggle.isPending}
                    onCheckedChange={(checked) =>
                      toggle.mutate({ eventType: t.event_type, mute: checked !== true })
                    }
                  />
                </div>
              )
            })}
          </div>
        </section>
      ))}
    </div>
  )
}
